import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, QrCode, AlertTriangle } from "lucide-react";
import { useOrganization } from "@/hooks/useOrganization";
import { usePartZoneStock } from "@/hooks/useFacilityZones";
import { useEntityImages } from "@/hooks/useEntityImages";
import { printPartLabel, buildMailtoUrl, generateQrDataUrl } from "@/lib/printPartLabel";
import type { LabelSize } from "@/lib/printPartLabel";
import type { Part } from "@/hooks/useParts";

interface PrintLabelDialogProps {
  part: Part;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function PrintLabelDialog({ part, open, onOpenChange }: PrintLabelDialogProps) {
  const { data: org } = useOrganization();
  const stock = usePartZoneStock(part.id);
  const { data: images = [] } = useEntityImages("part", part.id);
  const primaryImage = images.find((i) => i.is_primary) ?? images[0] ?? null;
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [labelSize, setLabelSize] = useState<LabelSize>("standard");

  const settings = (org?.settings as any) || {};
  const purchaser = {
    name: settings.parts_purchaser_name ?? null,
    email: settings.parts_purchaser_email ?? null,
  };

  const locations = stock.map((s) => ({
    facility: s.facility_name,
    zone: s.zone_name,
    quantity: s.quantity,
    unit: part.unit ?? "each",
  }));

  const labelData = {
    part,
    locations,
    purchaser,
    orgName: org?.name ?? null,
    orgLogoUrl: org?.logo_url ?? null,
    partImageUrl: primaryImage?.public_url ?? null,
    reorderQuantity: part.reorder_quantity ?? null,
  };

  // Generate QR preview whenever dialog opens or purchaser changes
  useEffect(() => {
    if (!open) return;
    const mailto = buildMailtoUrl(labelData);
    generateQrDataUrl(mailto).then(setQrDataUrl);
  }, [open, purchaser.email, part.id, org?.logo_url]);

  const handlePrint = async () => {
    setPrinting(true);
    try {
      await printPartLabel(labelData, labelSize);
    } finally {
      setPrinting(false);
    }
  };

  const missingPurchaser = !purchaser.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-4 w-4 text-muted-foreground" />
            Print Part Label
          </DialogTitle>
        </DialogHeader>

        {/* Label size toggle */}
        <div className="flex rounded-md border overflow-hidden text-sm">
          <button
            className={`flex-1 px-3 py-1.5 text-center transition-colors ${labelSize === "standard" ? "bg-primary text-primary-foreground font-medium" : "bg-background text-muted-foreground hover:bg-muted"}`}
            onClick={() => setLabelSize("standard")}
          >
            Standard — 4″ × 2.3″
          </button>
          <button
            className={`flex-1 px-3 py-1.5 text-center border-l transition-colors ${labelSize === "small" ? "bg-primary text-primary-foreground font-medium" : "bg-background text-muted-foreground hover:bg-muted"}`}
            onClick={() => setLabelSize("small")}
          >
            Small — 3.5″ × 1″
          </button>
        </div>

        {/* Label preview */}
        {labelSize === "standard" ? (
          <div className="rounded-lg border bg-white p-4 space-y-3">
            <div className="flex gap-4 items-start">
              {/* Info side */}
              <div className="flex-1 min-w-0 space-y-2">
                <div>
                  <p className="font-bold text-sm leading-tight">{part.name}</p>
                  {part.part_number && (
                    <p className="text-xs font-mono text-muted-foreground"># {part.part_number}</p>
                  )}
                </div>

                <div className="border-t pt-2 space-y-1.5">
                  <div>
                    <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Location</p>
                    {locations.length > 0 ? (
                      locations.map((l, i) => (
                        <p key={i} className="text-xs">{l.facility} › {l.zone} — {l.quantity} {l.unit}</p>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground">No locations assigned</p>
                    )}
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Min Qty</p>
                      <p className="text-xs">{part.min_quantity} {part.unit}</p>
                    </div>
                    {part.reorder_quantity != null && (
                      <div>
                        <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Reorder Qty</p>
                        <p className="text-xs">{part.reorder_quantity} {part.unit}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-1.5 flex items-center justify-between">
                  {org?.logo_url ? (
                    <img src={org.logo_url} alt="" className="h-5 max-w-[72px] object-contain" />
                  ) : (
                    <span className="text-[9px] font-semibold text-muted-foreground">{org?.name ?? ""}</span>
                  )}
                  <span className="text-[8px] text-muted-foreground">Scan to reorder</span>
                </div>
              </div>

              {/* QR + image side */}
              <div className="flex flex-col items-center gap-1 shrink-0">
                {primaryImage && (
                  <img
                    src={primaryImage.public_url}
                    alt=""
                    className="w-20 h-20 rounded object-cover"
                    style={{ filter: "grayscale(100%) contrast(1.1)" }}
                  />
                )}
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR Code" className="w-20 h-20 rounded" />
                ) : (
                  <div className="w-20 h-20 rounded bg-muted/50 flex items-center justify-center">
                    <QrCode className="h-8 w-8 text-muted-foreground/30" />
                  </div>
                )}
                <p className="text-[8px] text-muted-foreground text-center leading-tight">Scan to<br/>reorder</p>
              </div>
            </div>
          </div>
        ) : (
          /* Small label preview — matches 3.5:1 aspect ratio */
          <div className="rounded-lg border bg-white p-2">
            <div className="flex gap-2 items-center" style={{ height: 72 }}>
              {/* Info */}
              <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5 overflow-hidden pl-1">
                <p className="font-bold text-[11px] leading-tight truncate">{part.name}</p>
                {part.part_number && (
                  <p className="text-[9px] font-mono text-muted-foreground truncate"># {part.part_number}</p>
                )}
                <div className="border-t mt-1 pt-1 flex gap-3">
                  <div>
                    <p className="text-[8px] font-semibold uppercase tracking-wide text-muted-foreground">Min Qty</p>
                    <p className="text-[10px]">{part.min_quantity} {part.unit}</p>
                  </div>
                  {part.reorder_quantity != null && (
                    <div>
                      <p className="text-[8px] font-semibold uppercase tracking-wide text-muted-foreground">Reorder Qty</p>
                      <p className="text-[10px]">{part.reorder_quantity} {part.unit}</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Photo */}
              <div className="shrink-0 w-16 h-16">
                {primaryImage ? (
                  <img
                    src={primaryImage.public_url}
                    alt=""
                    className="w-16 h-16 rounded object-cover"
                    style={{ filter: "grayscale(100%) contrast(1.1)" }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded bg-muted/40" />
                )}
              </div>
              {/* QR */}
              <div className="shrink-0 w-16 h-16 pr-1">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR Code" className="w-16 h-16 rounded" />
                ) : (
                  <div className="w-16 h-16 rounded bg-muted/50 flex items-center justify-center">
                    <QrCode className="h-6 w-6 text-muted-foreground/30" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Purchaser info */}
        <div className={`rounded-lg px-3 py-2.5 text-xs space-y-0.5 ${missingPurchaser ? "bg-yellow-50 border border-yellow-200" : "bg-muted/50"}`}>
          {missingPurchaser ? (
            <div className="flex items-center gap-1.5 text-yellow-700">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              <span>No Parts Purchaser email set. QR code will have an empty "to" address. Set one in <strong>Settings → Organization</strong>.</span>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground">Reorder email will go to:</p>
              <p className="font-medium">{purchaser.name ?? purchaser.email} {purchaser.name && <span className="font-normal text-muted-foreground">({purchaser.email})</span>}</p>
            </>
          )}
        </div>

        {/* Instructions */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs space-y-1">
          <p className="font-semibold text-blue-800">To print:</p>
          <ol className="list-decimal list-inside space-y-0.5 text-blue-700">
            <li>Click <strong>Download PDF</strong> below</li>
            <li>Open the downloaded file in <strong>Preview</strong></li>
            <li>File → Print → Printer → <strong>DYMO LabelWriter 450</strong></li>
            <li>Paper size will be set automatically — just click <strong>Print</strong></li>
          </ol>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handlePrint} disabled={printing} className="gap-1.5">
            <Download className="h-4 w-4" />
            {printing ? "Generating…" : "Download PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
